import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";

// Types for company assets and configurations
export interface CompanyAsset {
  id: string;
  type: "logo" | "banner" | "icon" | "font" | "color";
  url?: string;
  value?: string;
  metadata?: Record<string, any>;
}

interface TableStyles {
  header: {
    background: string;
  };
  cell: {
    border: string;
    padding: string;
    width: string;
  };
  evenRow: {
    background: string;
  };
  alt: {
    background: string;
    borderColor: string;
  };
  form: {
    background: string;
    borderColor: string;
    firstCell: {
      width: string;
      textAlign: string;
      wordBreak: string;
      whiteSpace: string;
    };
  };
  holdings: {
    firstCell: {
      width: string;
    };
  };
}

interface CompanyStyles {
  subHeader: {
    color: string;
  };
  table: TableStyles;
  fonts: {
    primary: {
      family: string;
      url: string;
      weights: string;
    };
  };
}

interface CompanyInfo {
  email: string;
  fax: string;
  phone: string;
  address: string[];
  regulation: string;
  registration: string;
  fcaNumber: string;
  logoAlt?: string;
}

interface CompanySettings {
  dateFormat: string;
  timezone: string;
  styles: CompanyStyles;
  colors: string[];
  companyInfo: CompanyInfo;
}

export interface CompanyConfig {
  id: string;
  name: string;
  assets: CompanyAsset[];
  settings: CompanySettings;
  metadata?: Record<string, any>;
}

interface CompanyContextType {
  companyConfig: CompanyConfig | null;
  companyAssets: Record<string, CompanyAsset>;
  isLoading: boolean;
  error: Error | null;
  loadCompanyConfig: (config: string) => Promise<void>;
  companyId: string;
}

// Create the context with a default value
const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

// Cache for company configs to avoid repeated fetches
const configCache: Record<string, CompanyConfig> = {};

interface CompanyProviderProps {
  children: ReactNode;
  companyId: string;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({
  children,
  companyId,
}) => {
  const [companyConfig, setCompanyConfig] = useState<CompanyConfig | null>(
    null
  );
  const [companyAssets, setCompanyAssets] = useState<
    Record<string, CompanyAsset>
  >({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Process assets into a more usable format
  const processAssets = useCallback((assets: CompanyAsset[]) => {
    const processedAssets: Record<string, CompanyAsset> = {};

    assets.forEach((asset) => {
      processedAssets[`${asset.type}-${asset.id}`] = asset;
    });

    setCompanyAssets(processedAssets);
  }, []);

  // Function to load company configuration - memoized with useCallback
  const loadCompanyConfig = useCallback(
    async (config: string) => {
      if (!config) {
        setError(new Error("Config ID is required"));
        return;
      }

      // Check cache first
      if (configCache[config]) {
        setCompanyConfig(configCache[config]);
        processAssets(configCache[config].assets);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Mock API call - replace with actual API call
        // const response = await fetch(`/api/company-configs/${config}`);
        // const data = await response.json();

        // For now, using mock data
        const mockConfig: CompanyConfig = {
          id: config,
          name: "Octopus Money",
          assets: [
            {
              id: "primary",
              type: "logo",
              url: "/images/company/om-logo.png",
            },
            {
              id: "secondary",
              type: "logo",
              url: "/images/company/om-logo-alt.png",
            },
            {
              id: "background",
              type: "banner",
              url: "/images/company/om-visual.png",
            },
            {
              id: "primary-color",
              type: "color",
              value: "#e01b84",
            },
            {
              id: "secondary-color",
              type: "color",
              value: "#feb1fe",
            },
            {
              id: "line-color-1",
              type: "color",
              value: "#f050f7",
            },
            {
              id: "line-color-2",
              type: "color",
              value: "#0e002e",
            },
          ],
          settings: {
            dateFormat: "DD/MM/YYYY",
            timezone: "Europe/London",
            styles: {
              subHeader: {
                color: "#e01b84",
              },
              table: {
                header: {
                  background: "#feb1fe",
                },
                cell: {
                  border: "1px solid white",
                  padding: "6px 12px",
                  width: "fit-content",
                },
                evenRow: {
                  background: "#f3f3f3",
                },
                alt: {
                  background: "#ffffff",
                  borderColor: "#000000",
                },
                form: {
                  background: "#ffffff",
                  borderColor: "#000000",
                  firstCell: {
                    width: "20%",
                    textAlign: "left",
                    wordBreak: "normal",
                    whiteSpace: "wrap",
                  },
                },
                holdings: {
                  firstCell: {
                    width: "100%",
                  },
                },
              },
              fonts: {
                primary: {
                  family: "Montserrat",
                  url: "https://fonts.googleapis.com/css2?family=Montserrat:wght@100..900&display=swap",
                  weights: "100..900",
                },
              },
            },
            colors: [
              "#F050F7", // Pink
              "#0E012E", // Dark Blue
              "#FEB1FE", // Light Pink
              "#FFF27C", // Yellow
              "#C6F3FE", // Light Blue
              "#D0F99F", // Light Green
              "#D5A0FF", // Purple
              "#FF9B63", // Orange
              "#2A9A86", // Teal
              "#93C94E", // Green
              "#9B67B4", // Dark Purple
              "#F9F3F0", // Off White
            ],
            companyInfo: {
              email: "team@octopusmoney.com",
              fax: "020 3650 2050",
              phone: "020 3650 2050",
              logoAlt: "/images/company/om-logo-alt.png",
              address: [
                "Octopus Money",
                "7th Floor",
                "33 Holborn",
                "London",
                "EC1N 2HT",
                "UNITED KINGDOM",
              ],
              regulation:
                "Octopus Money Limited is authorised and regulated in the UK by the Financial Conduct Authority under 763630.",
              registration:
                "Octopus Money Limited is registered in England & Wales under No. 10339119 at 33 Holborn, London EC1N 2HT.",
              fcaNumber: "763630",
            },
          },
        };

        // Store in cache
        configCache[config] = mockConfig;

        setCompanyConfig(mockConfig);
        processAssets(mockConfig.assets);
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to load company config")
        );
      } finally {
        setIsLoading(false);
      }
    },
    [processAssets]
  );

  const value = {
    companyConfig,
    companyAssets,
    isLoading,
    error,
    loadCompanyConfig,
    companyId,
  };

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
};

// Custom hook to use the company context
export const useCompanyContext = () => {
  const context = useContext(CompanyContext);

  if (context === undefined) {
    throw new Error("useCompanyContext must be used within a CompanyProvider");
  }

  return context;
};

export const useCompanyId = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    return undefined;
  }
  return context.companyId;
};
